import stableStringify from 'json-stable-stringify';
import React, {
  ComponentProps,
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';
import { BreadcrumbContext } from '../../context';
import { BreadcrumbConfig, BreadcrumbPartType } from '../../types';
import { resolveBreadcrumbs } from './utils/resolveBreadcrumbs';
import { useGetEntityByRef, useGetEntityByRefCallback } from '../../hooks';

type Props = {
  breadcrumbDefinitions: ReadonlyArray<BreadcrumbConfig>;
};

export const BreadcrumbsProvider: FC<PropsWithChildren<Props>> = ({
  children,
  breadcrumbDefinitions,
}) => {
  const currentLocation = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState<
    ReadonlyArray<BreadcrumbPartType>
  >([]);
  const getEntityByRef = useGetEntityByRefCallback();

  const { kind, name, namespace } =
    matchPath(
      { path: '/catalog/:namespace/:kind/:name/*', end: false },
      currentLocation.pathname,
    )?.params ?? {};
  const { value: entity, loading: entityLoading } = useGetEntityByRef(
    kind && namespace && name ? { kind, namespace, name } : null,
  );

  const [breadcrumbsLoading, setBreadcrumbsLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    if (entity) {
      setBreadcrumbsLoading(true);
      resolveBreadcrumbs(entity, getEntityByRef, breadcrumbDefinitions)
        .then(value => {
          if (!isCancelled) {
            setBreadcrumbs(prevState => {
              if (stableStringify(prevState) === stableStringify(value)) {
                return prevState;
              }
              return value;
            });
            setBreadcrumbsLoading(false);
          }
        })
        .catch(() => {
          if (!isCancelled) {
            setBreadcrumbs([]);
            setBreadcrumbsLoading(false);
          }
        });
    } else {
      setBreadcrumbs([]);
      setBreadcrumbsLoading(false);
    }
    return () => {
      isCancelled = true;
    };
  }, [entity, getEntityByRef, breadcrumbDefinitions]);

  const loading = entityLoading || breadcrumbsLoading;
  const state = useMemo(
    (): ComponentProps<typeof BreadcrumbContext.Provider>['value'] => ({
      breadcrumbs,
      loading,
    }),
    [breadcrumbs, loading],
  );

  return (
    <BreadcrumbContext.Provider value={state}>
      {children}
    </BreadcrumbContext.Provider>
  );
};
